import type { ValidationErrors } from 'sonata-react-components';
import type { FormSection, FormSubsection } from '@/types/formSections.types';

/**
 * Mapping of schema field paths to their corresponding section and subsection.
 * Used to aggregate ValidationErrors into section-based error counts.
 */
const VALIDATION_ERROR_SECTION_MAP: Record<
  string,
  {
    section: FormSection;
    subsectionMap?: Record<string, FormSubsection>;
  }
> = {
  ads: { section: 'creatives', subsectionMap: { '': 'adCreatives' } },
  beginDate: { section: 'mainSettings', subsectionMap: { '': 'schedulingDistribution' } },
  countries: { section: 'locations', subsectionMap: { '': 'locationQualityIndexes' } },
  dco: { section: 'creatives', subsectionMap: { '': 'contextualSettings' } },
  endDate: { section: 'mainSettings', subsectionMap: { '': 'schedulingDistribution' } },
  flights: { section: 'mainSettings', subsectionMap: { '': 'flightSettings' } },
  goals: { section: 'mainSettings', subsectionMap: { '': 'budgetAndGoals' } },
  ioNumber: { section: 'mainSettings', subsectionMap: { '': 'ioSettings' } },
  locations: { section: 'locations', subsectionMap: { '': 'activationAreas' } },
  name: { section: 'mainSettings', subsectionMap: { '': 'ioSettings' } },
  pricingModelConfig: { section: 'mainSettings', subsectionMap: { '': 'pricingModel' } },
  scheduleParting: { section: 'mainSettings', subsectionMap: { '': 'schedulingDistribution' } },
  screens: { section: 'screens', subsectionMap: { '': 'screensSubsection' } },
  targeting: {
    section: 'contextTargeting',
    subsectionMap: {
      adExchanges: 'supplySource',
      appAndSiteMarketplace: 'appMarketplace',
      contextualTargeting: 'contextualBidding',
      retargeting: 'retargeting',
      temperatureAndWeather: 'temperatureAndWeather'
    }
  },
  timezone: { section: 'mainSettings', subsectionMap: { '': 'schedulingDistribution' } },
  type: { section: 'mainSettings', subsectionMap: { '': 'mediaChannel' } },
  userFrequencyCapping: { section: 'mainSettings', subsectionMap: { '': 'budgetAndGoals' } }
};

export type SectionErrorMap = {
  [section in FormSection]?: {
    total: number;
    subsections: {
      [subsection in FormSubsection]?: number;
    };
  };
};

/**
 * Converts ValidationErrors (nested field error objects) into a structured SectionErrorMap.
 * Aggregates error counts by section and subsection for sidebar badge display.
 *
 * @param errors ValidationErrors object from form validation
 * @returns SectionErrorMap with aggregated error counts per section and subsection
 */
export function validationErrorsToSectionErrorMap(errors: ValidationErrors = {}): SectionErrorMap {
  const errorMap: SectionErrorMap = {};

  const errorsList = errors || {};

  Object.keys(errorsList).forEach(fieldKey => {
    const fieldError = errorsList[fieldKey];

    if (!fieldError || typeof fieldError !== 'object') {
      return;
    }

    const pathConfig = VALIDATION_ERROR_SECTION_MAP[fieldKey];
    if (!pathConfig) {
      return;
    }

    const section = pathConfig.section;
    const subsectionMap = pathConfig.subsectionMap || {};

    // Initialize section if it doesn't exist
    if (!errorMap[section]) {
      errorMap[section] = {
        subsections: {},
        total: 0
      };
    }

    // Check for nested subsection errors (e.g., targeting.adExchanges)
    if (typeof fieldError === 'object' && fieldError !== null && !('message' in fieldError)) {
      // Multiple subsection errors
      Object.keys(fieldError).forEach(nestedKey => {
        const nestedError = (fieldError as Record<string, unknown>)[nestedKey];
        if (nestedError) {
          const subsection = subsectionMap[nestedKey] || subsectionMap[''] || fieldKey;
          if (!errorMap[section]!.subsections[subsection]) {
            errorMap[section]!.subsections[subsection] = 0;
          }
          errorMap[section]!.subsections[subsection]! += 1;
          errorMap[section]!.total += 1;
        }
      });
    } else {
      // Direct field error (single level)
      const subsection = subsectionMap[''] || fieldKey;
      if (!errorMap[section]!.subsections[subsection]) {
        errorMap[section]!.subsections[subsection] = 0;
      }
      errorMap[section]!.subsections[subsection]! += 1;
      errorMap[section]!.total += 1;
    }
  });

  return errorMap;
}
